import React, { useEffect, useMemo, useState } from 'react';
import DateTimeFilterBar from '../../components/dateTimeFilterBar';
import DropdownSearch from '../../components/dropdownSearch';
import Timezone from '../../components/timezone';
import Loading from '../../components/loading';
import API from '../../services/api';
import calendar from '../../utils/calendar';
import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import { getWidgetOptions } from '../../utils/widgets';
import colors from '../../assets/colors';
import StoreAttributionMap from './StoreAttributionMap';
import { campaignDateRange, categoryPoints, attributionMapPoints, storeViewRequest, timeSeries } from './storeUtils';
import { storeViewIds } from './storeTemplate';

const cards = [
  { id: 'store-exposed-devices', title: 'Exposed Devices', key: 'exposedDevices', format: 'integer' },
  { id: 'store-visits', title: 'Store Visits', key: 'storeVisits', format: 'integer' },
  { id: 'store-conversion-rate', title: 'Conversion Rate', key: 'conversionRate', format: 'float', unit: '%' }
];

const views = ['exposedDevices', 'storeVisits', 'conversionRate', 'attributedVisitors', 'exposedVsVisits',
  'topSites', 'topStores', 'map'];

const widgetInfo = {
  'store-exposed-devices': 'Distinct mobile devices exposed at campaign sites during the selected period.',
  'store-visits': 'Distinct mobile devices observed at target stores, including unattributed visitors.',
  'store-conversion-rate': 'Attributed visitors divided by exposed devices, using each target site attribution window.',
  'store-exposed-vs-visits': 'Daily exposed devices and target-store visitors.',
  'store-top-sites': 'Campaign sites ranked by distinct exposed devices.',
  'store-most-visited-stores': 'Target stores ranked by distinct attributed visitors.'
};

const Widget = ({ id, title, display, data, config, plotOptions, format, unit, loading, headerAction }) => (
  <widget-container
    id={id}
    widget-title={title}
    display={display}
    data={JSON.stringify(data)}
    config={JSON.stringify(config || {})}
    plot-options={plotOptions ? JSON.stringify(plotOptions) : undefined}
    format={format}
    unit={unit}
    is-loading={loading}
    info={JSON.stringify({ text: widgetInfo[id] })}
    options={getWidgetOptions({ display })}
  >
    {headerAction}
  </widget-container>
);

const loadCampaigns = async portal => {
  if (!portal.companyId) return [];
  const campaigns = [];
  const seen = new Set();
  let token = '';
  do {
    if (seen.has(token)) throw new Error('Repeated campaign page token');
    seen.add(token);
    const response = await API.getCampaigns(portal.companyId, token, encodeURI(`brandPortalId = "${portal.id}"`));
    campaigns.push(...(response.campaigns || []));
    token = response.nextPageToken || '';
  } while (token);
  return campaigns;
};

const StoreAttributionDashboard = ({ portal, lastUpdated, template }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [targetSites, setTargetSites] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [targetSiteId, setTargetSiteId] = useState('');
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState('');
  const [results, setResults] = useState({});
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [filters, setFilters] = useDashboardFilters({
    startDate: calendar.getDefaultDate('start'),
    endDate: calendar.getDefaultDate('end'),
    filterByTime: {},
    filterByWeekday: []
  });
  const reportTimezone = filters.isTimeRangeLocked && filters.timezone ? filters.timezone : timezone;
  const viewIds = storeViewIds;
  const configuredViews = views.filter(key => viewIds[key]);
  const visibleWidgets = new Set((template.rows || []).flatMap(row => (row.widgets || []).map(widget => widget.id)));
  const isVisible = id => !Array.isArray(template.rows) || visibleWidgets.has(id);

  useEffect(() => {
    let active = true;
    setFiltersLoading(true);
    setFiltersError('');
    Promise.all([loadCampaigns(portal), API.getTargetSites(portal.id)])
      .then(([nextCampaigns, sitesResponse]) => {
        if (!active) return;
        setCampaigns(nextCampaigns);
        setTargetSites(sitesResponse?.sites || []);
        setCampaignId(current => nextCampaigns.some(campaign => campaign.id === current) ? current : '');
        setTargetSiteId(current => (sitesResponse?.sites || []).some(site => site.id === current) ? current : '');
      })
      .catch(() => { if (active) setFiltersError('Campaigns and target sites could not be loaded.'); })
      .finally(() => { if (active) setFiltersLoading(false); });
    return () => { active = false; };
  }, [portal.id, portal.companyId]);

  useEffect(() => {
    if (!configuredViews.length || filtersLoading || filtersError) return undefined;
    let active = true;
    const request = storeViewRequest({ portalId: portal.id, startDate: filters.startDate, endDate: filters.endDate,
      timeZone: reportTimezone, campaignId, targetSiteId });
    setDataLoading(true);
    setDataError('');
    setResults({});
    Promise.allSettled(configuredViews.map(key => API.getAnalyticsViewData(viewIds[key], request)))
      .then(responses => {
        if (!active) return;
        const next = {};
        responses.forEach((response, index) => {
          if (response.status === 'fulfilled') next[configuredViews[index]] = response.value;
        });
        setResults(next);
        if (responses.some(response => response.status === 'rejected')) {
          setDataError('Some Screen to Store data could not be loaded.');
        }
      })
      .finally(() => { if (active) setDataLoading(false); });
    return () => { active = false; };
  }, [portal.id, filters.startDate, filters.endDate, reportTimezone, campaignId, targetSiteId, lastUpdated,
    filtersLoading, filtersError, template]);

  const campaignOptions = useMemo(() => [
    { value: '', label: 'All campaigns' },
    ...campaigns.map(campaign => ({ value: campaign.id, label: campaign.displayName || campaign.id }))
  ], [campaigns]);
  const targetOptions = useMemo(() => [
    { value: '', label: 'All sites' },
    ...targetSites.map(site => ({ value: site.id, label: site.displayName || site.id }))
  ], [targetSites]);
  const topSites = categoryPoints(results.topSites);
  const topStores = categoryPoints(results.topStores);
  const trend = timeSeries(results.exposedVsVisits, reportTimezone);
  const mapPoints = useMemo(() => attributionMapPoints(results.map), [results.map]);
  const conversionCount = results.attributedVisitors?.scalar?.value;
  const [showConversionCount, setShowConversionCount] = useState(false);
  const conversionNotApplicable = results.conversionRate?.coverage?.notApplicableReason === 'zero_denominator';

  const onCampaignSelected = nextCampaignId => {
    setCampaignId(nextCampaignId);
    if (filters.isTimeRangeLocked) return;
    const campaign = campaigns.find(item => item.id === nextCampaignId);
    const range = campaignDateRange(campaign);
    if (range) setFilters(current => ({ ...current, ...range }));
  };

  return (
    <main id="web-attribution-container" className="store-attribution-dashboard" data-last-updated={lastUpdated}>
      <div className="filter-bar">
        <div className="filters store-attribution-filters">
          <DateTimeFilterBar key={`${filters.startDate}-${filters.endDate}`} appliedFilters={filters}
            onPeriodSelected={next => setFilters(current => ({ ...current, ...next }))}
            hideIntervalSelector />
          <DropdownSearch label="Campaign" selected={campaignOptions.find(option => option.value === campaignId)}
            options={campaignOptions} onSelect={onCampaignSelected} disabled={filtersLoading || Boolean(filtersError)} leftAlign />
          <DropdownSearch label="Sites" selected={targetOptions.find(option => option.value === targetSiteId)}
            options={targetOptions} onSelect={setTargetSiteId} disabled={filtersLoading || Boolean(filtersError)} leftAlign />
          <Timezone label="Timezone" leftAlign onSelect={setTimezone}
            selected={{ value: reportTimezone, label: reportTimezone }}
            disabled={Boolean(filters.isTimeRangeLocked && filters.timezone)} />
        </div>
      </div>
      <div className="scrollable-container web-attribution-scroll">
        <div id="web-attribution-chart-container">
          {filtersLoading || (dataLoading && !filtersError) ? (
            <div className="store-attribution-widget-loading" role="status">
              <Loading size={45} />
              <span>Loading Screen to Store data...</span>
            </div>
          ) : filtersError ? (
            <div className="store-attribution-message" role="alert">{filtersError}</div>
          ) : <>
          {dataError && <div className="store-attribution-message" role="alert">{dataError}</div>}
          {cards.some(card => isVisible(card.id)) && <section className="chart-row web-attribution-metrics store-attribution-metrics"
            aria-label="Screen to Store summary">
            {cards.filter(card => isVisible(card.id)).map(card => {
              const value = card.key === 'conversionRate' && showConversionCount
                ? conversionCount
                : card.key === 'conversionRate'
                  ? results.conversionRate?.scalar?.decimalValue
                  : results[card.key]?.scalar?.value;
              const notApplicable = card.key === 'conversionRate' && conversionNotApplicable && !showConversionCount;
              const toggle = card.key === 'conversionRate' && (
                <button className="btn btn-link store-attribution-toggle" slot="header-left"
                  disabled={conversionCount === undefined} onClick={() => setShowConversionCount(current => !current)}>
                  {showConversionCount ? 'Show rate' : 'Show number'}
                </button>
              );
              return <div className={`store-attribution-metric ${notApplicable ? 'is-na' : ''}`} key={card.id}>
                <Widget id={card.id} title={card.title} display="digit"
                  data={notApplicable ? [{ label: card.title, value: 'N/A' }] :
                    value === undefined ? [] : [{ label: card.title, value: Number(value) }]}
                  format={notApplicable ? undefined : card.key === 'conversionRate' && showConversionCount ? 'integer' : card.format}
                  unit={notApplicable || card.key === 'conversionRate' && showConversionCount ? undefined : card.unit}
                  loading={dataLoading} headerAction={toggle} />
                {notApplicable && <span className="store-attribution-na-note">No exposed devices in the selected period.</span>}
              </div>;
            })}
          </section>}
          {isVisible('store-exposed-vs-visits') && <section className="chart-row">
            <Widget id="store-exposed-vs-visits" title="Exposed Devices vs. Store Visits" display="line"
              data={trend.data} config={{ colors: [colors.mainPallet[0], colors.mainPallet[3]], height: 260 }}
              plotOptions={{ categories: trend.categories }} loading={dataLoading} />
          </section>}
          {(isVisible('store-top-sites') || isVisible('store-most-visited-stores')) &&
          <section className="chart-row web-attribution-split-row">
            {isVisible('store-top-sites') && <Widget id="store-top-sites" title="Top Performing Sites" display="stats-list"
              data={topSites.map(point => ({ label: point.label, value: point.value }))}
              config={{ height: 270 }} plotOptions={{ displayBars: true }} loading={dataLoading} />}
            {isVisible('store-most-visited-stores') && <Widget id="store-most-visited-stores" title="Most Visited Stores" display="stats-list"
              data={topStores.map(point => ({ label: point.label, value: point.value }))}
              config={{ height: 270 }} plotOptions={{ displayBars: true }} loading={dataLoading} />}
          </section>}
          {isVisible('store-attribution-map') && <section className="chart-row">
            <div className="card store-attribution-map-card">
              <div className="card-header"><h5>Site and Store Map</h5></div>
              <div className="card-body"><StoreAttributionMap sites={mapPoints} /></div>
            </div>
          </section>}
          </>}
        </div>
      </div>
    </main>
  );
};

export default StoreAttributionDashboard;
